import APITemplate from '@api/APITemplate'
import { formatQueryParams } from '@utils/Format'
import store from '@state/store'
import con from '@services/connection'
import { receitasOuDespesas } from '@modules/financial-planning/constants/URL'
import { shouldReuseCachedSlice } from '@modules/financial-planning/utils/fpCacheScope'

function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}
class APIFinancialPlanningReceitasOuDespesas extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = {}
  }

  _shouldSkipLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    // O cache é por cliente: sem comparar `receitasOuDespesasClientId` o slice
    // do cliente anterior era devolvido para o novo por até 1 minuto.
    const reuseCache = shouldReuseCachedSlice({
      storedClientId:
        store.getters['financialPlanning/receitasOuDespesasClientId'],
      clientId,
      lastUpdate:
        store.getters['financialPlanning/receitasOuDespesasLastUpdate'],
      loading: store.getters['financialPlanning/receitasOuDespesasLoading'],
      forceUpdate,
    })

    if (reuseCache) {
      return store.getters['financialPlanning/receitasOuDespesas']
    }

    return null
  }

  _handleResponse(response, clientId) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setReceitasOuDespesas', {
        items,
        clientId,
      })
    } else {
      store.dispatch('financialPlanning/setReceitasOuDespesas', {
        items: [],
        clientId: null,
      })
    }
  }

  async _executeCarregar(clientId, requestKey, requestUrl) {
    store.dispatch('financialPlanning/setReceitasOuDespesasLoading', true)
    this.reqCarregar[requestKey] = con.get(requestUrl)

    try {
      const response = await this.reqCarregar[requestKey]
      this._handleResponse(response, clientId)
      return response
    } catch (error) {
      this._handleResponse(null, clientId)
      throw error
    } finally {
      store.dispatch('financialPlanning/setReceitasOuDespesasLoading', false)
      delete this.reqCarregar[requestKey]
    }
  }

  async carregar({ clientId, params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    const query = getQueryString(params)
    const requestKey = query ? `${clientId}:${query}` : `${clientId}`

    if (this.reqCarregar[requestKey]) {
      return this.reqCarregar[requestKey]
    }

    const requestUrl = `${receitasOuDespesas.list(clientId)}${query}`
    const response = await this._executeCarregar(clientId, requestKey, requestUrl)

    return response || {}
  }

  async salvar({ clientId, params }) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const payload = {
      data: JSON.stringify(params),
    }

    const response = await con.post(receitasOuDespesas.list(clientId), payload)

    if (response && response.status === 200) {
      const value = response.value

      if (value) {
        store.dispatch(
          'financialPlanning/addReceitasOuDespesas',
          value[0] || value || {}
        )
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async atualizar({ clientId, id, params } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!id) {
      return {
        status: 404,
        message: 'Item não encontrado.',
      }
    }

    if (!params || typeof params !== 'object') {
      return {
        status: 400,
        message: 'Dados inválidos para receita ou despesa.',
      }
    }

    const payload = {
      data: JSON.stringify(params),
    }

    const response = await con.put(
      receitasOuDespesas.detail(clientId, id),
      payload
    )

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addReceitasOuDespesas', {
          item: value,
          clientId,
        })
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async remover({ clientId, id }) {
    if (!id) {
      return {
        status: 404,
        message: 'Item não encontrado.',
      }
    }

    const targetClientId =
      clientId || store.getters['financialPlanning/receitasOuDespesasClientId']

    if (!targetClientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    store.dispatch('financialPlanning/setReceitasOuDespesasLoading', true)

    const response = await con.delet(
      receitasOuDespesas.detail(targetClientId, id)
    )

    store.dispatch('financialPlanning/setReceitasOuDespesasLoading', false)

    if (response && response.status === 200) {
      await this.carregar({ clientId: targetClientId, forceUpdate: true })
    }

    return response || {}
  }
}

const apiFinancialPlanningReceitasOuDespesas =
  new APIFinancialPlanningReceitasOuDespesas()
export { apiFinancialPlanningReceitasOuDespesas }
